import diwaliProductImage from '../assets/images/diwali/diwali-combo-hero.png';
import diwaliDesktopBackgroundImage from '../assets/images/diwali/diwali-bg-desktop.png';
import diwaliMobileBackgroundImage from '../assets/images/diwali/diwali-bg-mobile.png';
import { products } from '../utils/productData';
import { calculateDiscountPercentage, calculateSavingsAmount } from '../utils/pricing';

export type CampaignTheme = 'diwali';
export type CampaignMediaType = 'image';

export type CampaignCTA = {
  label: string;
  href: string;
};

export type CampaignColors = {
  primary: string;
  secondary: string;
  accent: string;
  deep: string;
  soft: string;
};

const glowCombo = products.find((product) => product.id === 3);
const maxDiscount = Math.max(
  ...products.map((product) => calculateDiscountPercentage(product.originalPrice, product.price) ?? 0)
);
const formatCurrency = (amount: number) => `₹${amount.toLocaleString('en-IN')}`;
const glowComboPrice = glowCombo ? formatCurrency(glowCombo.price) : '';
const glowComboOriginalPrice = glowCombo?.originalPrice ? formatCurrency(glowCombo.originalPrice) : '';
const glowComboSavings = glowCombo ? calculateSavingsAmount(glowCombo.originalPrice, glowCombo.price) : null;
const glowComboDiscount = glowCombo ? calculateDiscountPercentage(glowCombo.originalPrice, glowCombo.price) : null;

export type CampaignConfig = {
  campaignName: string;
  campaignLabel: string;
  campaignTheme: CampaignTheme;
  heading: string;
  subHeading: string;
  description: string;
  accentColors: CampaignColors;
  gradientColors: { hero: string; announcement: string };
  heroDesktopImage: string;
  heroTabletImage: string;
  heroMobileImage: string;
  heroDesktopBackgroundImage: string;
  heroMobileBackgroundImage: string;
  featuredPromoMedia: string;
  heroImageFit: 'contain' | 'cover';
  heroImagePosition: string;
  heroMediaType: CampaignMediaType;
  heroMediaAlt: string;
  offerPercentage: number;
  announcementText: string;
  primaryCTA: CampaignCTA;
  secondaryCTA: CampaignCTA;
  featuredComboName: string;
  featuredComboPrice: string;
  featuredComboOriginalPrice: string;
  featuredComboSavings: string;
  featuredComboDiscount: number | null;
  featuredComboCtaText: string;
  featuredComboHref: string;
  offerChips: string[];
  trustPoints: string[];
  showAnnouncementBar: boolean;
  showOfferBadge: boolean;
  showFloatingComboCard: boolean;
  showTrustStrip: boolean;
  showSecondaryCTA: boolean;
  showRating: boolean;
  showSavings: boolean;
  showOriginalPrice: boolean;
  showPriceCard: boolean;
  backgroundEffects: { glow: boolean };
};

export const campaign: CampaignConfig = {
  campaignName: 'MaySecret The Diwali Glow Edit',
  campaignLabel: 'MaySecret • The Diwali Glow Edit',
  campaignTheme: 'diwali',
  heading: 'Light Up Your Glow',
  subHeading: 'Celebrate the Festival of Lights.',
  description: 'Discover Korean-inspired skincare essentials made to bring a little more glow to your everyday routine.',
  accentColors: {
    primary: '#501B32',
    secondary: '#32143C',
    accent: '#C9A45E',
    deep: '#24102D',
    soft: '#FFF9F2',
  },
  gradientColors: {
    hero: 'linear-gradient(122deg, #24102D 0%, #32143C 52%, #24102D 100%)',
    announcement: 'linear-gradient(90deg, #24102D 0%, #501B32 50%, #24102D 100%)',
  },
  heroDesktopImage: diwaliProductImage,
  heroTabletImage: diwaliProductImage,
  heroMobileImage: diwaliProductImage,
  heroDesktopBackgroundImage: diwaliDesktopBackgroundImage,
  heroMobileBackgroundImage: diwaliMobileBackgroundImage,
  featuredPromoMedia: diwaliProductImage,
  heroImageFit: 'contain',
  heroImagePosition: 'center',
  heroMediaType: 'image',
  heroMediaAlt: 'MaySecret Sunscreen Spray and Rice Brightening Serum in a Diwali skincare composition',
  offerPercentage: maxDiscount,
  announcementText: 'The Diwali Glow Edit ✦ Discover Your Festive Skincare ✦ Free Shipping',
  primaryCTA: { label: 'Shop Diwali Glow', href: '/shop' },
  secondaryCTA: { label: 'Explore Glow Combos', href: '#diwali-glow-combos' },
  featuredComboName: 'Glow Combo Pack',
  featuredComboPrice: glowComboPrice,
  featuredComboOriginalPrice: glowComboOriginalPrice,
  featuredComboSavings: glowComboSavings === null ? '' : `Save ${formatCurrency(glowComboSavings)}`,
  featuredComboDiscount: glowComboDiscount,
  featuredComboCtaText: 'Shop Glow Combo',
  featuredComboHref: '/product/3',
  offerChips: [`Up to ${maxDiscount}% Off`, 'Combo Packs from ₹899', 'Free Shipping', 'Cash on Delivery'],
  trustPoints: ['Korean Inspired', 'Premium Ingredients', 'Cruelty Free', 'Customer Favourite'],
  showAnnouncementBar: true,
  showOfferBadge: false,
  showFloatingComboCard: true,
  showTrustStrip: true,
  showSecondaryCTA: true,
  showRating: true,
  showSavings: true,
  showOriginalPrice: true,
  showPriceCard: true,
  backgroundEffects: { glow: false },
};
